/* ------------------------------------------------------------------
   CRMPage.js — contacts / leads list (Marketing → CRM)
   Stats cards · search + filters · table · add/edit modal · CSV export.
   Follows the app's relative-/api + withCredentials fetch convention.
------------------------------------------------------------------ */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Modal } from 'react-bootstrap';

const ax = axios.create({ withCredentials: true });

const STATUS_META = {
  lead:       { label: 'Lead',       bg: '#fef3c7', color: '#92400e' },
  subscriber: { label: 'Subscriber', bg: '#dbeafe', color: '#1e40af' },
  customer:   { label: 'Customer',   bg: '#dcfce7', color: '#166534' },
};

export default function CRMPage() {
  const [contacts, setContacts] = useState([]);
  const [stats, setStats]       = useState({ total: 0, lead: 0, subscriber: 0, customer: 0 });
  const [loading, setLoading]   = useState(true);
  const [msg, setMsg]           = useState(null);

  const [search, setSearch]     = useState('');
  const [statusF, setStatusF]   = useState('');
  const [sourceF, setSourceF]   = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing]     = useState(null);
  const [form, setForm]           = useState({ email: '', name: '', status: 'lead', tags: '', notes: '' });
  const [saving, setSaving]       = useState(false);

  const [showImport, setShowImport] = useState(false);
  const [importRows, setImportRows] = useState(null); // {contacts, invalid, total, error?}
  const [importing, setImporting]   = useState(false);

  const debounceRef = useRef(null);
  const fileRef     = useRef(null);

  const flash = (text, ok = true) => { setMsg({ text, ok }); setTimeout(() => setMsg(null), 3000); };

  const loadContacts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (statusF) params.status = statusF;
      if (sourceF) params.source = sourceF;
      const { data } = await ax.get('/api/crm/contacts', { params });
      setContacts(data.contacts || []);
    } catch (e) {
      console.error('load contacts', e);
    } finally {
      setLoading(false);
    }
  }, [search, statusF, sourceF]);

  const loadStats = useCallback(async () => {
    try { const { data } = await ax.get('/api/crm/stats'); setStats(data); } catch { /* noop */ }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(loadContacts, 200);
    return () => clearTimeout(debounceRef.current);
  }, [loadContacts]);

  /* ── modal ── */
  function openAdd() {
    setEditing(null);
    setForm({ email: '', name: '', status: 'lead', tags: '', notes: '' });
    setShowModal(true);
  }
  function openEdit(c) {
    setEditing(c);
    setForm({ email: c.email, name: c.name || '', status: c.status, tags: (c.tags || []).join(', '), notes: c.notes || '' });
    setShowModal(true);
  }
  async function saveContact() {
    setSaving(true);
    const tags = form.tags.split(',').map(t => t.trim()).filter(Boolean);
    try {
      if (editing) {
        await ax.put(`/api/crm/contacts/${editing.id}`, { name: form.name, status: form.status, tags, notes: form.notes });
      } else {
        await ax.post('/api/crm/contacts', { email: form.email, name: form.name, status: form.status, tags, notes: form.notes });
      }
      setShowModal(false);
      flash('Saved');
      loadContacts(); loadStats();
    } catch (e) {
      flash(e.response?.data?.error || 'Save failed', false);
    } finally {
      setSaving(false);
    }
  }
  async function deleteContact(c) {
    if (!window.confirm(`Delete ${c.email}?`)) return;
    try { await ax.delete(`/api/crm/contacts/${c.id}`); loadContacts(); loadStats(); }
    catch { flash('Delete failed', false); }
  }

  /* ── CSV import ── */
  function onImportFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setImportRows(parseCsvToContacts(String(reader.result || '')));
    reader.readAsText(file);
    if (fileRef.current) fileRef.current.value = '';
  }
  async function doImport() {
    if (!importRows || !importRows.contacts?.length) return;
    setImporting(true);
    try {
      const { data } = await ax.post('/api/crm/contacts/import', { contacts: importRows.contacts });
      setShowImport(false); setImportRows(null);
      flash(`Imported ${data.imported} contact${data.imported === 1 ? '' : 's'}${data.invalid ? ` (${data.invalid} skipped)` : ''}`);
      loadContacts(); loadStats();
    } catch (e) {
      flash(e.response?.data?.error || 'Import failed', false);
    } finally {
      setImporting(false);
    }
  }

  /* ── CSV export (blob so it works through the dev proxy + sends cookie) ── */
  async function exportCsv() {
    try {
      const res = await ax.get('/api/crm/contacts/export.csv', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'contacts.csv'; a.click();
      URL.revokeObjectURL(url);
    } catch { flash('Export failed', false); }
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 12, marginBottom: 18 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: '0 0 4px' }}>CRM</h1>
          <p style={{ color: '#6b7280', fontSize: 14, margin: 0 }}>Your contacts and leads, all in one place.</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => { setImportRows(null); setShowImport(true); }} style={ghostBtn}>Import CSV</button>
          <button onClick={exportCsv} style={ghostBtn}>Export CSV</button>
          <button onClick={openAdd} style={primaryBtn}>+ Add contact</button>
        </div>
      </div>

      {msg && (
        <div style={{ marginBottom: 14, padding: '10px 14px', borderRadius: 8, fontSize: 14, fontWeight: 600,
          background: msg.ok ? '#ecfdf5' : '#fef2f2', color: msg.ok ? '#059669' : '#dc2626',
          border: `1px solid ${msg.ok ? '#a7f3d0' : '#fecaca'}` }}>{msg.text}</div>
      )}

      {/* Stat cards */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
        <Stat label="Total contacts" value={stats.total} />
        <Stat label="Leads"       value={stats.lead} />
        <Stat label="Subscribers" value={stats.subscriber} />
        <Stat label="Customers"   value={stats.customer} />
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name or email…"
          style={{ ...inputStyle, maxWidth: 280 }} />
        <select value={statusF} onChange={e => setStatusF(e.target.value)} style={selectStyle}>
          <option value="">All statuses</option>
          <option value="lead">Lead</option>
          <option value="subscriber">Subscriber</option>
          <option value="customer">Customer</option>
        </select>
        <select value={sourceF} onChange={e => setSourceF(e.target.value)} style={selectStyle}>
          <option value="">All sources</option>
          <option value="link-in-bio">Link in Bio</option>
          <option value="manual">Manual</option>
          <option value="import">Imported</option>
        </select>
      </div>

      {/* Table */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>
        ) : contacts.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#9ca3af' }}>
            No contacts yet. Add one manually, or turn on email capture in Link in Bio to start collecting leads.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#f8fafc', color: '#6b7280', textAlign: 'left' }}>
                <Th>Name</Th><Th>Email</Th><Th>Status</Th><Th>Source</Th><Th>Added</Th><Th></Th>
              </tr>
            </thead>
            <tbody>
              {contacts.map(c => {
                const sm = STATUS_META[c.status] || STATUS_META.lead;
                return (
                  <tr key={c.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                    <Td>{c.name || <span style={{ color: '#9ca3af' }}>—</span>}</Td>
                    <Td>{c.email}</Td>
                    <Td><span style={{ fontSize: 12, fontWeight: 600, padding: '2px 9px', borderRadius: 99, background: sm.bg, color: sm.color }}>{sm.label}</span></Td>
                    <Td><span style={{ fontSize: 12, color: '#6b7280' }}>{c.source === 'link-in-bio' ? '🔗 Link in Bio' : c.source === 'import' ? '⬆ Imported' : 'Manual'}</span></Td>
                    <Td><span style={{ color: '#6b7280' }}>{String(c.created_at || '').slice(0, 10)}</span></Td>
                    <Td>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                        <button onClick={() => openEdit(c)} style={miniBtn}>Edit</button>
                        <button onClick={() => deleteContact(c)} style={{ ...miniBtn, color: '#dc2626', borderColor: '#fecaca' }}>Delete</button>
                      </div>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Add/Edit modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton><Modal.Title style={{ fontSize: 18 }}>{editing ? 'Edit contact' : 'Add contact'}</Modal.Title></Modal.Header>
        <Modal.Body>
          <Label>Email</Label>
          <input value={form.email} disabled={!!editing}
            onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
            placeholder="name@email.com" style={{ ...inputStyle, opacity: editing ? 0.6 : 1 }} />

          <Label style={{ marginTop: 14 }}>Name</Label>
          <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Full name" style={inputStyle} />

          <Label style={{ marginTop: 14 }}>Status</Label>
          <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} style={selectStyle}>
            <option value="lead">Lead</option>
            <option value="subscriber">Subscriber</option>
            <option value="customer">Customer</option>
          </select>

          <Label style={{ marginTop: 14 }}>Tags (comma-separated)</Label>
          <input value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))} placeholder="vip, newsletter" style={inputStyle} />

          <Label style={{ marginTop: 14 }}>Notes</Label>
          <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
        </Modal.Body>
        <Modal.Footer>
          <button onClick={() => setShowModal(false)} style={ghostBtn}>Cancel</button>
          <button onClick={saveContact} disabled={saving} style={{ ...primaryBtn, opacity: saving ? 0.6 : 1 }}>{saving ? 'Saving…' : 'Save'}</button>
        </Modal.Footer>
      </Modal>

      {/* Import modal */}
      <Modal show={showImport} onHide={() => setShowImport(false)} centered>
        <Modal.Header closeButton><Modal.Title style={{ fontSize: 18 }}>Import contacts</Modal.Title></Modal.Header>
        <Modal.Body>
          <p style={{ fontSize: 14, color: '#6b7280', marginTop: 0 }}>
            Upload a CSV with an <strong>email</strong> column. Optional columns: name, status, tags, notes.
            (Matches the Export format — tags separated by <code>|</code>.)
          </p>
          <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={onImportFile} style={{ fontSize: 14 }} />
          {importRows && (
            importRows.error
              ? <div style={{ marginTop: 12, color: '#dc2626', fontSize: 14 }}>{importRows.error}</div>
              : <div style={{ marginTop: 12, fontSize: 14, color: '#111827' }}>
                  <strong>{importRows.contacts.length}</strong> valid contact{importRows.contacts.length === 1 ? '' : 's'} ready
                  {importRows.invalid ? `, ${importRows.invalid} invalid row${importRows.invalid === 1 ? '' : 's'} will be skipped` : ''}.
                </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button onClick={() => setShowImport(false)} style={ghostBtn}>Cancel</button>
          <button onClick={doImport} disabled={importing || !importRows || !importRows.contacts?.length}
            style={{ ...primaryBtn, opacity: (importing || !importRows || !importRows.contacts?.length) ? 0.6 : 1 }}>
            {importing ? 'Importing…' : `Import ${importRows?.contacts?.length || 0}`}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

/* Parse a CSV string → { contacts, invalid, total, error? }.
   Handles quoted fields, escaped quotes, and newlines inside quotes.
   Maps columns case-insensitively by header (email required). */
function parseCsvToContacts(text) {
  const rows = [];
  let i = 0, field = '', row = [], inQ = false;
  while (i < text.length) {
    const ch = text[i];
    if (inQ) {
      if (ch === '"') { if (text[i + 1] === '"') { field += '"'; i += 2; continue; } inQ = false; i++; continue; }
      field += ch; i++; continue;
    }
    if (ch === '"') { inQ = true; i++; continue; }
    if (ch === ',') { row.push(field); field = ''; i++; continue; }
    if (ch === '\r') { i++; continue; }
    if (ch === '\n') { row.push(field); rows.push(row); row = []; field = ''; i++; continue; }
    field += ch; i++;
  }
  if (field.length || row.length) { row.push(field); rows.push(row); }
  if (!rows.length) return { contacts: [], invalid: 0, total: 0, error: 'The file is empty.' };

  const header = rows[0].map(h => String(h).trim().toLowerCase());
  const find = re => header.findIndex(h => re.test(h));
  const emailIdx = find(/e-?mail/);
  if (emailIdx === -1) return { contacts: [], invalid: 0, total: 0, error: 'No "email" column found in the CSV header.' };
  const nameIdx   = header.findIndex(h => /name/.test(h) && !/(email|user|file|company)/.test(h));
  const statusIdx = find(/status/);
  const tagsIdx   = find(/tag/);
  const notesIdx  = find(/note/);

  const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
  const get = (cells, idx) => (idx > -1 ? String(cells[idx] || '').trim() : '');
  const contacts = [];
  let invalid = 0;
  for (let r = 1; r < rows.length; r++) {
    const cells = rows[r];
    if (!cells || cells.every(c => !String(c).trim())) continue; // blank line
    const email = get(cells, emailIdx);
    if (!EMAIL.test(email)) { invalid++; continue; }
    contacts.push({
      email,
      name: get(cells, nameIdx),
      status: get(cells, statusIdx).toLowerCase(),
      tags: get(cells, tagsIdx),
      notes: get(cells, notesIdx),
    });
  }
  return { contacts, invalid, total: rows.length - 1 };
}

/* ── helpers ── */
const inputStyle  = { width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, color: '#111827', outline: 'none', boxSizing: 'border-box' };
const selectStyle = { ...inputStyle, width: 'auto', minWidth: 150, background: '#fff' };
const primaryBtn  = { padding: '9px 16px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' };
const ghostBtn    = { padding: '9px 14px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' };
const miniBtn     = { padding: '5px 10px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer' };
const Label = ({ children, style }) => <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6, ...style }}>{children}</div>;
const Th = ({ children }) => <th style={{ padding: '10px 14px', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em' }}>{children}</th>;
const Td = ({ children }) => <td style={{ padding: '11px 14px', color: '#111827' }}>{children}</td>;
const Stat = ({ label, value }) => (
  <div style={{ flex: '1 1 130px', minWidth: 120, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '14px 16px' }}>
    <div style={{ fontSize: 24, fontWeight: 700, color: '#111827' }}>{Number(value).toLocaleString()}</div>
    <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: 2 }}>{label}</div>
  </div>
);
